import { PlusOutlined } from '@ant-design/icons'
import { Alert, Button, message, Spin, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useRolodexSignedURLs } from 'features/rolodex'
import type { SectionAddendum } from 'pages/reporting/reports/rug/rugReportData'
import { getAddendumPages } from 'pages/reporting/reports/rug/utils/addendums/addendumContent'
import { inspectAttachmentFile } from 'pages/reporting/reports/rug/utils/addendums/attachmentFiles'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { SectionAddendumFields } from './SectionAddendumFields'

const Container = styled.div`
  padding: 1.5rem;
  max-width: 75rem;
`
const FormContent = styled.div`
  margin-top: 1.5rem;
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`

interface Props {
  initialAddendums: SectionAddendum[]
  sectionOptions: { value: string; label: string }[]
  metadataEntryId: string
  readOnly: boolean
  readOnlyReason: string | undefined
  onSave: (addendums: SectionAddendum[]) => Promise<void>
}

const withIds = (addendums: SectionAddendum[]) =>
  addendums.map((addendum) => ({
    ...addendum,
    id: addendum.id ?? crypto.randomUUID(),
  }))

export function SectionAddendumsEditor({
  initialAddendums,
  sectionOptions,
  metadataEntryId,
  readOnly,
  readOnlyReason,
  onSave,
}: Props) {
  const [addendums, setAddendums] = useState(() => withIds(initialAddendums))
  const [pendingUploads, setPendingUploads] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const mounted = useRef(true)
  const { rolodexUploadFile } = useRolodexEdit()
  useEffect(() => {
    setAddendums(withIds(initialAddendums))
  }, [initialAddendums])
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const busy = saving || pendingUploads > 0
  const { results, errors, isLoading } = useRolodexSignedURLs(
    addendums.flatMap((addendum) =>
      (addendum.attachments ?? []).map((file) => ({
        entry_id: metadataEntryId,
        path: file.path,
      }))
    )
  )
  const urls = new Map(
    results.map((result) => [result.path, result.signed_url])
  )

  const uploadFile = async (file: File) => {
    setPendingUploads((count) => count + 1)
    try {
      if (readOnly) throw new Error('Void signatures before editing addendums')
      const attachment = await inspectAttachmentFile(file)
      if (!mounted.current)
        throw new DOMException('Revision changed', 'AbortError')
      await rolodexUploadFile(metadataEntryId, file, attachment.path)
      if (!mounted.current)
        throw new DOMException('Revision changed', 'AbortError')
      return attachment
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        message.error(
          error instanceof Error ? error.message : 'Failed to upload attachment'
        )
      }
    } finally {
      if (mounted.current) setPendingUploads((count) => count - 1)
    }
  }
  const add = () =>
    setAddendums((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        sectionId: '',
        pageSubtitle: '',
        addedPageCount: 1,
        mode: 'attachments',
        attachments: [],
      },
    ])
  const save = async () => {
    if (readOnly || busy) return
    setError(undefined)
    try {
      for (const addendum of addendums) {
        if (!addendum.sectionId.trim())
          throw new Error(
            'Choose a section or enter a custom section name for each addendum'
          )
        if (getAddendumPages(addendum).length === 0)
          throw new Error('Add at least one attachment or select blank pages')
      }
      setSaving(true)
      await onSave(
        addendums.map((addendum) => ({
          ...addendum,
          sectionId: addendum.sectionId.trim(),
        }))
      )
    } catch (error) {
      if (mounted.current)
        setError(
          error instanceof Error ? error.message : 'Failed to save addendums'
        )
    } finally {
      if (mounted.current) setSaving(false)
    }
  }
  const fields = addendums.map((addendum, index) => {
    const change = (update: (current: SectionAddendum) => SectionAddendum) =>
      setAddendums((current) =>
        current.map((item) =>
          item.id === addendum.id ? { ...update(item), id: item.id } : item
        )
      )
    const remove = () =>
      setAddendums((current) =>
        current.filter((item) => item.id !== addendum.id)
      )
    return (
      <SectionAddendumFields
        key={addendum.id}
        addendum={addendum}
        index={index}
        sectionOptions={sectionOptions}
        disabled={readOnly || busy}
        urls={urls}
        onChange={change}
        onRemove={remove}
        uploadFile={uploadFile}
      />
    )
  })

  return (
    <Container>
      <Typography.Title level={2}>Section Addendums</Typography.Title>
      <Typography.Paragraph type='secondary'>
        Add calculation screenshots, PDFs, or blank pages to existing or custom
        sections. Save your changes, then regenerate the draft before signing.
      </Typography.Paragraph>
      {readOnly && (
        <Alert
          type='info'
          showIcon
          title='Addendums are read-only'
          description={readOnlyReason}
        />
      )}
      {error && (
        <Alert
          type='error'
          showIcon
          title='Addendums could not be saved'
          description={error}
        />
      )}
      {pendingUploads > 0 && (
        <Typography.Paragraph>
          <Spin size='small' /> Uploading attachment…
        </Typography.Paragraph>
      )}
      {isLoading && (
        <Typography.Paragraph type='secondary'>
          Loading attachment previews…
        </Typography.Paragraph>
      )}
      {errors.some(Boolean) && (
        <Alert
          type='warning'
          showIcon
          title='Attachment previews could not be loaded'
          description='Reload this section to try again. Your saved attachments are unchanged.'
        />
      )}
      <FormContent>{fields}</FormContent>
      <Actions>
        <Button
          icon={<PlusOutlined />}
          onClick={add}
          disabled={readOnly || busy}>
          Add section addendum
        </Button>
        <Button
          type='primary'
          onClick={save}
          loading={saving}
          disabled={readOnly || busy}>
          Save section addendums
        </Button>
      </Actions>
    </Container>
  )
}
